import React from 'react';

import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button, { IconButton } from '@atlaskit/button/new';
import { Anchor } from '@atlaskit/primitives/compiled';

import type { PanelActionProps } from './types';

/**
 * The PanelAction component provides a primitive base for building panel action buttons.
 * It handles the basic rendering logic for buttons vs links and supports both regular
 * buttons and icon-only buttons. Use this component to build specific action variants.
 */
export const PanelAction: React.ForwardRefExoticComponent<
	PanelActionProps & React.RefAttributes<HTMLButtonElement | HTMLAnchorElement>
> = React.forwardRef<HTMLButtonElement | HTMLAnchorElement, PanelActionProps>(
	(
		{
			children,
			onClick,
			href,
			testId,
			target,
			rel,
			'aria-label': ariaLabel,
			'aria-haspopup': ariaHaspopup,
			'aria-expanded': ariaExpanded,
			icon,
			label,
			appearance = 'subtle',
			spacing = 'default',
		},
		ref,
	): React.JSX.Element => {
		if (icon) {
			return (
				<IconButton
					ref={ref as React.Ref<HTMLButtonElement>}
					onClick={
						onClick as (
							e: React.MouseEvent<HTMLButtonElement>,
							analyticsEvent: UIAnalyticsEvent,
						) => void
					}
					testId={testId}
					icon={icon}
					label={label || ariaLabel}
					appearance={appearance}
					aria-haspopup={ariaHaspopup}
					aria-expanded={ariaExpanded}
					isTooltipDisabled={false}
					spacing={spacing}
				/>
			);
		}

		if (href) {
			return (
				<Anchor
					ref={ref as React.Ref<HTMLAnchorElement>}
					href={href}
					onClick={
						onClick as (
							e: React.MouseEvent<HTMLAnchorElement>,
							analyticsEvent: UIAnalyticsEvent,
						) => void
					}
					testId={testId}
					target={target}
					rel={rel}
					aria-label={ariaLabel}
					aria-haspopup={ariaHaspopup}
					aria-expanded={ariaExpanded}
				>
					{children}
				</Anchor>
			);
		}

		return (
			<Button
				ref={ref as React.Ref<HTMLButtonElement>}
				onClick={
					onClick as (
						e: React.MouseEvent<HTMLButtonElement>,
						analyticsEvent: UIAnalyticsEvent,
					) => void
				}
				testId={testId}
				aria-label={ariaLabel}
				aria-haspopup={ariaHaspopup}
				aria-expanded={ariaExpanded}
				appearance={appearance}
			>
				{children}
			</Button>
		);
	},
);
